import React, { useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { buildUser, buildWorkerProfile } from "../../factories";
import { ProfileType, WorkerProfileInput, WorkingPermitType } from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProvider } from "@apollo/client/testing/react";
import CivilStateEditorScreen from "../../../screens/profile-editor/CivilStateEditor";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { UPDATE_WORKER_PROFILE } from "../../../graphql/core";
import { StoryStackNavigatorContainer } from "../../display-helpers";

const Stack = createNativeStackNavigator<{
    CivilStateEdit: undefined;
}>();

const StoryComponent = ({
    workPermit,
    nationality,
}: {
    workPermit?: WorkingPermitType;
    nationality: string;
}) => {
    const user = useMemo(() => {
        const user = buildUser({
            workerProfile: buildWorkerProfile({
                swissOrHasPermitAnswer: !!workPermit,
                lastWorkPermit: { type: workPermit ?? null, validFrom: null, validTo: null },
                avsNumber: null,
            }),
            nationality,
        });
        return user;
    }, [nationality, workPermit]);

    const mocks = useMemo(
        () => [
            {
                request: {
                    query: UPDATE_WORKER_PROFILE,
                    variables: () => true,
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: ({ changes }: { changes: WorkerProfileInput }) => {
                    // eslint-disable-next-line no-console
                    console.log("UPDATE_WORKER_PROFILE.changes", changes);
                    return {
                        data: {
                            updateWorkerProfile: buildWorkerProfile({
                                children: [],
                                hasFiscalAttestation: false,
                            }),
                        },
                    };
                },
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="CivilStateEdit">
                            <Stack.Screen
                                name="CivilStateEdit"
                                component={CivilStateEditorScreen}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/ProfileEditor/CivilStateEditor",
    component: StoryComponent,
    argTypes: {
        workPermit: {
            options: Object.values(WorkingPermitType),
            control: { type: "radio" },
            defaultValue: WorkingPermitType.PermitB,
        },
        nationality: {
            options: ["ch", "fr", "us"],
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const FR_B: Story = { args: { workPermit: WorkingPermitType.PermitB, nationality: "fr" } };
export const CH: Story = { args: { workPermit: undefined, nationality: "ch" } };
export const FR_no_permit: Story = { args: { workPermit: undefined, nationality: "fr" } };
